import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { RowAction } from 'components/enhanced/types/enhanced.types';
import { ToolbarAction, ToolbarFilter } from 'components/enhanced/types/toolbar.types';
import { equipmentActivityService } from 'features/inventory/equipments/api/equipment-activity.service';
import { IEquipment } from 'features/inventory/equipments/types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoiceOption } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { ActivitiesColumns } from './ActivitiesColumns';
import { ActivityFormData, ActivityFormDialog } from './ActivityFormDialog';

interface ActivityTabProps {
  equipment: { id: number | string; code?: string } | IEquipment | null;
}
// Stato iniziale vuoto per i form (Create / Edit)
const initialFormState: ActivityFormData = {
  type: '',
  date: new Date().toISOString().split('T')[0],
  validationDate: new Date().toISOString().split('T')[0],
  description: '',
  notes: '',
  operatorName: '',
  operatorRole: ''
};

export const ActivityTab = ({ equipment }: ActivityTabProps) => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.equipment.logbook.activities';

  const datagridRefresh = useRef<() => void>(null);
  // Stati della Tabella e dei Filtri
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [filterType, setFilterType] = useState('');
  const [typeChoices, setTypeChoices] = useState<ChoiceOption[]>([]);

  // Stati dei Modali e ID Selezionato
  const [selectedActivityId, setSelectedActivityId] = useState<number | string | null>(null);
  const [isCreateModelOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Stato Unificato del Form (Sostituisce gli stati atomici multipli)
  const [formData, setFormData] = useState<ActivityFormData>(initialFormState);

  const columns: GridColDef[] = ActivitiesColumns(datagridRefresh);

  useEffect(() => {
    loadTypeOptions();
  }, []);

  const loadTypeOptions = () => {
    equipmentActivityService.options().then((res) =>
      setTypeChoices(
        res.actions?.POST?.activity_type?.choices.map((choise) => {
          return {
            ...choise,
            display_name: choise.display_name
          };
        }) || []
      )
    );
  };

  const handleFieldChange = (field: keyof ActivityFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateModalOpen = () => {
    setFormData({
      ...initialFormState,
      date: new Date().toISOString().split('T')[0],
      validationDate: new Date().toISOString().split('T')[0]
    });
    setIsCreateModalOpen(true);
    loadTypeOptions();
  };

  const handleCreate = async () => {
    if (!equipment?.id) return;
    setIsSubmitting(true);

    try {
      await equipmentActivityService.add({
        equipment: Number(equipment.id),
        activity_type: formData.type,
        description: formData.description,
        notes: formData.notes,
        activity_date: formData.date,
        validation_date: formData.validationDate
      } as any);

      toast.success('Attività registrata con successo!');
      setIsCreateModalOpen(false);
      setFormData(initialFormState);
      datagridRefresh.current?.();
    } catch (err) {
      console.error(err);
      toast.error("Impossibile salvare l'attività. Verifica i dati inseriti e riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedActivityId) return;
    setIsSubmitting(true);

    const payload = {
      activity_type: formData.type,
      description: formData.description,
      notes: formData.notes,
      activity_date: formData.date,
      validation_date: formData.validationDate
    };

    try {
      await equipmentActivityService.update(selectedActivityId, payload);
      toast.success('Attività aggiornata con successo!');
      setIsEditModalOpen(false);
      setSelectedActivityId(null);
      setFormData(initialFormState);
      datagridRefresh.current?.();
    } catch (err) {
      console.error("Errore durante l'aggiornamento dell'attività:", err);
      toast.error("Impossibile salvare l'attività. Verifica i dati inseriti e riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------- TOOLBAR ----------------------------

  const toolbarFilters: ToolbarFilter[] = React.useMemo(
    () => [
      {
        id: 'activity_date',
        type: 'dateRange',
        value: dateRange,
        onChange: (val) => setDateRange(val),
        gridField: 'activity_date',
        gridOperators: ['onOrAfter', 'onOrBefore']
      },
      {
        id: 'activity_type',
        type: 'select',
        label: t(`${translationPrefix}.activity_type`),
        value: filterType,
        onChange: (val) => setFilterType(val),
        gridField: 'activity_type',
        gridOperator: 'is',
        options: typeChoices.map((c) => ({ value: c.value, label: c.display_name }))
      }
    ],
    [dateRange, filterType, typeChoices]
  );

  const toolbarActions: ToolbarAction[] = [
    {
      id: 'add',
      label: t(`${translationPrefix}.add_activity`),
      variant: 'contained',
      startIcon: <AddIcon />,
      onClick: handleCreateModalOpen
    }
  ];

  // ---------------------------- ACTIONS ----------------------------

  const activityRowActions: RowAction[] = [
    {
      id: 'edit-record',
      label: t('actions.edit'),
      icon: <EditIcon />,
      onClick: (params: any) => {
        const row = params?.row ? params.row : params;
        if (!row) return;

        setSelectedActivityId(row.id);

        setFormData({
          type: row.activity_type ?? '',
          description: row.description ?? '',
          notes: row.notes ?? '',
          date: row.activity_date ? row.activity_date.split('T')[0] : '',
          validationDate: row.validation_date ? row.validation_date.split('T')[0] : '',
          operatorName: row.operator_name ?? '',
          operatorRole: row.operator_role ?? ''
        });

        setIsEditModalOpen(true);
      }
    }
  ];

  return (
    <>
      <EnhancedDataGrid
        columns={columns}
        service={equipmentActivityService}
        refresh={datagridRefresh}
        rowActions={activityRowActions}
        outerParams={{
          equipment: equipment?.id
        }}
        useGenericToolbar={true}
        genericToolbarConfig={{
          filters: toolbarFilters,
          actions: toolbarActions
        }}
        showGridControls={false}
      />

      {/* DIALOG DI CREAZIONE NUOVA ATTIVITÀ */}
      <ActivityFormDialog
        open={isCreateModelOpen}
        isCreation={true}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreate}
        isLoading={isSubmitting}
        title={t(`${translationPrefix}.create_activity_title`)}
        subtitle={t(`${translationPrefix}.create_informative_subtitle`)}
        formData={formData}
        onFieldChange={handleFieldChange}
        typeChoices={typeChoices}
      />

      {/* DIALOG DI MODIFICA RECORD ESISTENTE */}
      <ActivityFormDialog
        open={isEditModalOpen}
        isCreation={false}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdate}
        isLoading={isSubmitting}
        title={`Modifica attività #${String(selectedActivityId ?? '').padStart(2, '0')}`}
        subtitle={t(`${translationPrefix}.update_informative_subtitle`)}
        formData={formData}
        onFieldChange={handleFieldChange}
        typeChoices={typeChoices}
      />
    </>
  );
};
